/*
 * Copyright (c) 2025 - 2026. Bluemate B.V.
 *
 * The "Add notification" wizard, built on the generic BluemateStepper:
 *   step 1 — pick a trigger type (cards with icon, badges and description)
 *   step 2 — pick the delivery channels: e-mail with an editable address, and
 *            push with a per-device checkbox list (all subscribed devices are
 *            pre-selected; the current browser appears as a subscribable
 *            "Dit apparaat" option — disabled in a mobile browser outside the
 *            installed app, where subscribing is not allowed)
 *   step 3 — ALWAYS shown after creation: a success screen offering a test
 *            send through exactly the selected channels (test e-mail, test
 *            push to the selected devices, or both)
 * The step-2 button creates the notification (the controller PATCHes its
 * channels + per-notification device targeting) and advances to step 3.
 * When an active notification of the same type already overlaps the selected
 * channels, an interception overlay warns about duplicate messages first.
 */
import './style/addNotification.scss';

import React, {ForwardedRef, forwardRef, MouseEventHandler, useContext, useImperativeHandle, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {hasAdminLogin} from '../../../util/adminUtil';

import {NotificationType} from '../../../util/enums';

import {ActivateHandle} from '../../../util/interfaces';

import {AwesomeIcon, getSetting, ResponseData, showSnackbar, validateEmail} from '@dvrd/dvr-controls';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import classNames from 'classnames';

import BluemateBadgePremium from '../bluemateBadgePremium';

import {BluemateBadge, BluemateButton, BluemateCheckbox, BluemateInput} from '../../controls';

import BluemateStepper, {BluemateStepperContext, BluemateStepperStep} from '../../controls/stepper/bluemateStepper';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import {PortalSelectItem, PortalSelectOption, PortalSelectValue} from '../../../util/types';

import {
    computeWizardPushTargets,
    currentDeviceFormFactor,
    deviceDisplayName,
    deviceOptionValue,
    deviceTypeIcon,
    isCurrentDevice,
    isDeviceOptionValue,
    readNotificationChannels,
    THIS_DEVICE_VALUE
} from '../../../util/pushChannelUtil';

import {listDeviceTokens, sendTestMailToRecipient, sendTestPushToDevice} from '../../../util/requestSender';

import {onShowConfirmSnackbar} from '../../../util/renderUtil';

import {getOptedIn} from '../../../hooks/usePushSubscription';

import {PushChannelsContext} from '../../pushNotifications/pushChannelsContext';

import {showPushPermissionGuide} from '../../pushNotifications/pushPermissionGuide';

import {isMobileDevice} from '../../../util/pwaUtil';

// Aliased: importing the model as `Notification` would shadow the browser's
// global Notification API used for the permission checks below.
import NotificationModel, {FREE_NOTIFICATION_TYPES} from '../../../models/notificationModel';

import {AppContext} from '../../misc/appContext';

export interface AddNotificationSubmit {
    notificationType: NotificationType;
    emailEnabled: boolean;
    emailAddress: string;
    pushEnabled: boolean;
    /** Per-notification device targeting for the created notification: ALWAYS
     *  the explicit token-id list of the wizard selection (never null = "all
     *  devices" — null targeting silently adopts every future-subscribed
     *  device, which made the first push enable light up every
     *  configuration). Non-empty whenever pushEnabled is true. */
    pushDeviceIds: Array<string>;
}

interface AddNotificationValues {
    notificationType: NotificationType | null;
    emailEnabled: boolean;
    emailAddress: string;
    pushEnabled: boolean;
    /** Selected SUBSCRIBED device ids (defaults to all of them). */
    deviceIds: Array<string>;
    /** The synthetic "Dit apparaat" option — this browser has no subscription
     *  yet; checking it runs the subscribe flow on submit. */
    thisDeviceSelected: boolean;
}

interface Props {
    /** Creates the notification (and PATCHes its channels/targeting). Resolves
     *  true once it actually landed — the wizard only advances to the success
     *  step then. A false result (create failed, or an early-return such as a
     *  premium type without an active module) keeps the user on the channels
     *  step; the controller surfaces the reason. */
    onActivateModule: MouseEventHandler;
    onSubmit: (values: AddNotificationSubmit) => Promise<boolean>;
    moduleActive: boolean;
    notifications: Array<NotificationModel>;
}

/** A footer-button label that collapses to a short form on narrow screens (CSS
 *  toggles the two spans at the mobile breakpoint) — e.g. "Previous step" → "Back",
 *  "Add notification" → "Add" — so the step-2 footer fits on a phone. */
function responsiveLabel(full: string, short: string) {
    return (
        <>
            <span className='stepper-label-full'>{full}</span>
            <span className='stepper-label-short'>{short}</span>
        </>
    );
}

function AddNotification(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {onSubmit, moduleActive, notifications, onActivateModule} = props;
    const appContext = useContext(AppContext);
    const pushContext = useContext(PushChannelsContext);
    const [active, setActive] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // In-flight state of the success step's test send (spins the button).
    const [testSending, setTestSending] = useState(false);
    // Values pending the duplicate-warning overlay; null = overlay hidden.
    const [pendingValues, setPendingValues] = useState<AddNotificationValues | null>(null);

    const defaultEmail = appContext.accountContext.account?.email ?? appContext.userContext.user?.email ?? '';
    const userId = appContext.userContext.user?.id ?? null;
    const accountId = appContext.accountContext.account?.id ?? null;

    // The push channel is gated by the `enableWebPush` feature flag (kill
    // switch) — same as every other push surface (notificationsPage,
    // notificationChannelsSelect, pushChannelsContext). With it off the wizard
    // must offer no push channel and never run the subscribe flow.
    const enableWebPush = getSetting('enableWebPush') === true;
    // Backend truth for the device options: the account's subscribed devices.
    const subscribedDevices = pushContext?.devices ?? [];
    const currentEndpoint = pushContext?.currentEndpoint ?? null;
    const currentDeviceSubscribed = subscribedDevices.some(device => isCurrentDevice(device, currentEndpoint));
    // Mobile ban: a mobile browser OUTSIDE the installed app (PWA) may not
    // mint a push subscription — the synthetic "Dit apparaat" option is
    // disabled there (canUseHere is false; inside the installed app it is
    // true, so the PWA can subscribe normally).
    const canSubscribeHere =
        enableWebPush && !!pushContext && pushContext.state.isSupported && pushContext.state.canUseHere;
    // The current browser is offered as a subscribable option only while it
    // has no token row yet; once subscribed it is one of subscribedDevices.
    const syntheticOffered = !currentDeviceSubscribed;
    const pushPossible = enableWebPush && (subscribedDevices.length > 0 || canSubscribeHere);

    // The channels step's stepper context — the duplicate overlay's confirm
    // button lives OUTSIDE the step render, yet must advance to the success
    // step after submitting.
    const stepContextRef = React.useRef<BluemateStepperContext<AddNotificationValues> | null>(null);
    // Explicit targeting the created notification was submitted with — the
    // success step's test button sends to exactly this set.
    const submittedTargetsRef = React.useRef<Array<string>>([]);

    function onActivate() {
        setPendingValues(null);
        setSubmitting(false);
        setTestSending(false);
        submittedTargetsRef.current = [];
        setActive(true);
    }

    function onClose() {
        if (submitting) return;
        setActive(false);
        setPendingValues(null);
    }

    /** Same type AND at least one overlapping channel — the duplicate rule the
     *  warning overlay guards. */
    function hasDuplicate(values: AddNotificationValues): boolean {
        const selected = [...(values.emailEnabled ? ['EMAIL'] : []), ...(values.pushEnabled ? ['WEB_PUSH'] : [])];
        return notifications.some(notification => {
            if (notification.notificationType !== values.notificationType) return false;
            const channels = readNotificationChannels(notification);
            return selected.some(channel => channels.includes(channel as (typeof channels)[number]));
        });
    }

    function onCreate(context: BluemateStepperContext<AddNotificationValues>) {
        stepContextRef.current = context;
        if (hasDuplicate(context.values)) {
            setPendingValues(context.values);
            return;
        }
        void submit(context.values);
    }

    /** The freshly-subscribed current device's token id. The provider refresh
     *  usually delivers the row within a beat of enable(); retry a direct
     *  device-list fetch (matched on this browser's push endpoint) so the
     *  explicit targeting list can include the new device. */
    async function resolveCurrentTokenId(): Promise<string | null> {
        const fromContext = pushContext?.devices.find(device => isCurrentDevice(device, pushContext.currentEndpoint));
        if (fromContext) return fromContext.id;
        if (!accountId || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
        try {
            const registration = await navigator.serviceWorker.getRegistration('/');
            const subscription = (await registration?.pushManager.getSubscription()) ?? null;
            if (!subscription) return null;
            for (let attempt = 0; attempt < 3; attempt++) {
                const id = await new Promise<string | null>(resolve => {
                    listDeviceTokens({
                        account_id: accountId,
                        callback: (data: ResponseData) => {
                            const rows = ((data as {tokens?: Array<{id: string; endpoint?: string | null}>}).tokens ||
                                (data as {device_tokens?: Array<{id: string; endpoint?: string | null}>})
                                    .device_tokens ||
                                []) as Array<{id: string; endpoint?: string | null}>;
                            resolve(rows.find(row => row.endpoint === subscription.endpoint)?.id ?? null);
                        },
                        errorCallback: () => resolve(null)
                    });
                });
                if (id) return id;
                await new Promise(resolveDelay => setTimeout(resolveDelay, 500));
            }
        } catch (_err) {
            /* fall through — caller falls back to all-devices targeting */
        }
        return null;
    }

    async function submit(values: AddNotificationValues) {
        if (!values.notificationType) return;
        setSubmitting(true);
        try {
            const runsSubscribeFlow = values.pushEnabled && values.thisDeviceSelected && !currentDeviceSubscribed;
            if (runsSubscribeFlow && pushContext) {
                // The device is not subscribed yet: run the full subscribe flow
                // first (native permission prompt included) — the notification
                // should only be created with a push channel that can deliver.
                if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
                    showPushPermissionGuide(t, 'denied');
                    return;
                }
                const enableError = await pushContext.enable();
                if (enableError === 'device-limit') {
                    showSnackbar(
                        t('push.errors.deviceLimit', 'Maximaal aantal apparaten bereikt. Verwijder er eerst een.'),
                        {backgroundColor: 'color-blue-bluemate', textColor: 'white'}
                    );
                    return;
                }
                if (!getOptedIn(userId, accountId)) {
                    showSnackbar(t('push.notGranted', 'Meldingen niet ingeschakeld: geen toestemming gegeven.'), {
                        backgroundColor: 'color-blue-bluemate',
                        textColor: 'white'
                    });
                    return;
                }
            }
            // The wizard selection mapped to the stored targeting: ALWAYS an
            // explicit id list — never null (= "all devices incl. future
            // ones", which made every configuration light up on the first
            // push enable). A freshly-subscribed current device is appended
            // once its token id resolves.
            let pushEnabled = values.pushEnabled;
            let pushDeviceIds: Array<string> = [];
            if (values.pushEnabled) {
                pushDeviceIds = computeWizardPushTargets({
                    selectedIds: values.deviceIds,
                    subscribedIds: subscribedDevices.map(device => device.id)
                });
                if (runsSubscribeFlow) {
                    const currentId = await resolveCurrentTokenId();
                    if (currentId && !pushDeviceIds.includes(currentId)) {
                        pushDeviceIds = [...pushDeviceIds, currentId];
                    }
                }
                if (!pushDeviceIds.length) {
                    // No resolvable push target (the fresh token id never
                    // appeared): never fall back to all-devices targeting.
                    // With e-mail on, degrade to e-mail only and tell the
                    // user; push-only keeps the user on the channels step.
                    if (!values.emailEnabled) {
                        showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                            backgroundColor: 'color-blue-bluemate',
                            textColor: 'white'
                        });
                        return;
                    }
                    pushEnabled = false;
                    showSnackbar(
                        t(
                            'notifications.add.pushTargetUnresolved',
                            'Pushmelding kon niet aan dit apparaat gekoppeld worden. Schakel push in op de notificatiekaart.'
                        ),
                        {backgroundColor: 'color-blue-bluemate', textColor: 'white'}
                    );
                }
            }
            submittedTargetsRef.current = pushDeviceIds;
            // Await the create: the success step ("Notificatie toegevoegd" +
            // test send) must only appear once the notification actually
            // landed. A failed create — or an early-return in the controller —
            // resolves false and keeps the user on the channels step.
            const created = await onSubmit({
                notificationType: values.notificationType,
                emailEnabled: values.emailEnabled,
                emailAddress: values.emailAddress.trim() || defaultEmail,
                pushEnabled,
                pushDeviceIds
            });
            setPendingValues(null);
            if (created) stepContextRef.current?.next();
        } finally {
            setSubmitting(false);
        }
    }

    /** The success step's test send: exercises EXACTLY the selected channels —
     *  a test e-mail to the configured address, a test push to every targeted
     *  device, or both. */
    async function onSendTest(values: AddNotificationValues) {
        if (testSending || !accountId) return;
        setTestSending(true);
        try {
            const jobs: Array<Promise<{success: boolean; code?: string}>> = [];
            if (values.emailEnabled) {
                jobs.push(
                    new Promise(resolve => {
                        sendTestMailToRecipient({
                            email_address: values.emailAddress.trim() || defaultEmail,
                            account_id: accountId,
                            callback: (data: ResponseData) =>
                                resolve({success: !!data.success, code: (data as {error_code?: string}).error_code}),
                            errorCallback: () => resolve({success: false})
                        });
                    })
                );
            }
            if (values.pushEnabled) {
                // The explicit targeting the notification was created with —
                // empty when push degraded to e-mail only (no jobs then).
                const targetIds = submittedTargetsRef.current;
                for (const tokenId of targetIds) {
                    jobs.push(
                        new Promise(resolve => {
                            sendTestPushToDevice({
                                token_id: tokenId,
                                account_id: accountId,
                                callback: (data: ResponseData) =>
                                    resolve({
                                        success: !!data.success,
                                        code: (data as {error_code?: string}).error_code
                                    }),
                                errorCallback: () => resolve({success: false})
                            });
                        })
                    );
                }
            }
            const results = await Promise.all(jobs);
            const failed = results.find(result => !result.success);
            if (!failed) {
                onShowConfirmSnackbar(t('push.testSent', 'Testmelding is verzonden'));
            } else {
                const fallback = t('push.error', 'Er ging iets mis, probeer het opnieuw');
                showSnackbar(failed.code ? t(`push.testErrors.${failed.code}`, fallback) : fallback, {
                    backgroundColor: 'color-blue-bluemate',
                    textColor: 'white'
                });
            }
        } finally {
            setTestSending(false);
        }
    }

    function renderTypeCard(
        context: BluemateStepperContext<AddNotificationValues>,
        notificationType: NotificationType,
        config: {icon: IconName; tone: string; beta?: boolean; label: string; description: string}
    ) {
        const premium = !FREE_NOTIFICATION_TYPES.includes(notificationType);
        const disabled = premium && !moduleActive && !hasAdminLogin();

        function onPick() {
            if (disabled) return;
            context.setValues({notificationType});
            context.next();
        }

        return (
            <div
                className={classNames('add-type-card', disabled && 'disabled')}
                onClick={onPick}
                role='button'
                tabIndex={disabled ? -1 : 0}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') onPick();
                }}
            >
                <div className={classNames('add-type-icon', `tone-${config.tone}`)}>
                    <AwesomeIcon name={config.icon} />
                </div>
                <div className='add-type-texts'>
                    <span className='add-type-title'>
                        {config.label} {premium && <BluemateBadgePremium />}{' '}
                        {config.beta && <BluemateBadge color='blue' label={t('notifications.add.betaBadge', 'Beta')} />}
                    </span>
                    <span className='add-type-description'>{config.description}</span>
                    {disabled && (
                        <span className='add-type-disabled-hint'>
                            {t('notifications.add.premiumBadge', 'Premium notificatie')}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    /** The gold upsell, rendered in the dialog header's top-right (PWA-13 —
     *  it replaced the "+€5 per maand" info banner). Only while the premium
     *  module is inactive: with it active there is nothing to add. */
    function renderPremiumHeaderAction(): React.ReactNode {
        if (moduleActive) return null;
        return (
            <BluemateButton
                onClick={onActivateModule}
                label={t('notifications.add.addPremium', 'Premium toevoegen...')}
                className='btn-add-premium'
                color='gold'
                shape='square'
            />
        );
    }

    function renderTypeStep(context: BluemateStepperContext<AddNotificationValues>) {
        return (
            <>
                {renderTypeCard(context, NotificationType.BUY_BOX, {
                    icon: 'trophy',
                    tone: 'blue',
                    label: t('notifications.add.buyBoxLabel', 'Koopblok notificatie'),
                    description: t(
                        'notifications.add.buyBoxDescription',
                        'Ontvang een e-mail wanneer het koopblok verandert'
                    )
                })}
                {renderTypeCard(context, NotificationType.STOCK, {
                    icon: 'box-open',
                    tone: 'amber',
                    label: t('notifications.add.stockLabel', 'Voorraad notificatie'),
                    description: t(
                        'notifications.add.stockDescription',
                        'Ontvang een e-mail als de voorraad bijna op is'
                    )
                })}
                {renderTypeCard(context, NotificationType.PRODUCT_HIJACK, {
                    icon: 'user-shield',
                    tone: 'red',
                    // PWA-12: Product-hijack moved out of beta a while ago — no Beta badge.
                    label: t('notifications.add.productHijackLabel', 'Productkaping notificatie'),
                    description: t(
                        'notifications.add.productHijackDescription',
                        'Ontvang een e-mail wanneer een concurrent ook als verkoper bij jouw product verschijnt.'
                    )
                })}
                {renderTypeCard(context, NotificationType.REVIEWS, {
                    icon: 'star',
                    tone: 'yellow',
                    label: t('notifications.add.reviewLabel', 'Review notificatie'),
                    description: t('notifications.add.reviewDescription', 'Ontvang een e-mail bij nieuwe reviews')
                })}
                {renderTypeCard(context, NotificationType.ORDER, {
                    icon: 'truck-fast',
                    tone: 'green',
                    label: t('notifications.add.orderLabel', 'Order notificatie'),
                    description: t('notifications.add.orderDescription', 'Ontvang een e-mail bij nieuwe orders')
                })}
            </>
        );
    }

    function buildDeviceItems(): Array<PortalSelectItem<string>> {
        const labels = {
            installedApp: t('push.installedApp', 'geïnstalleerde app'),
            unknownDevice: t('push.unknownDevice', 'Onbekend apparaat'),
            thisDevice: {
                mobile: t('push.thisPhone', 'Deze telefoon'),
                tablet: t('push.thisTablet', 'Deze tablet'),
                desktop: t('push.thisComputer', 'Deze computer')
            },
            formFactor: {
                mobile: t('push.formFactorPhone', 'Telefoon'),
                tablet: t('push.formFactorTablet', 'Tablet'),
                desktop: t('push.formFactorComputer', 'Computer')
            }
        };
        const items: Array<PortalSelectOption<string>> = subscribedDevices.map(device => ({
            value: deviceOptionValue(device.id),
            disabled: submitting,
            label: (
                <span className='portal-select-item-label channel-option'>
                    <AwesomeIcon name={deviceTypeIcon(device)} className='channel-option-icon' />
                    {deviceDisplayName(device, labels, isCurrentDevice(device, currentEndpoint))}
                </span>
            )
        }));
        if (syntheticOffered) {
            // This browser has no subscription yet: offer it as an option that
            // subscribes on submit. Mobile browsers outside the installed app
            // may not subscribe (the mobile push ban) — disabled with a hint.
            items.push({
                value: THIS_DEVICE_VALUE,
                disabled: submitting || !canSubscribeHere,
                label: (
                    <span className='portal-select-item-label channel-option'>
                        <AwesomeIcon
                            name={isMobileDevice() ? 'mobile-screen-button' : 'desktop'}
                            className='channel-option-icon'
                        />
                        {canSubscribeHere
                            ? labels.thisDevice[currentDeviceFormFactor()]
                            : t('notifications.components.thisDeviceAppOnly', '{{device}} (alleen in de app)', {
                                  device: labels.thisDevice[currentDeviceFormFactor()]
                              })}
                    </span>
                )
            });
        }
        return items;
    }

    function parseDevicesValue(value: PortalSelectValue<string>): string {
        const selected = Array.isArray(value) ? value : [value];
        const count = selected.length;
        if (!count) return t('notifications.add.devicesNone', 'Selecteer apparaten');
        return `${count} ${
            count === 1
                ? t('notifications.components.deviceSingular', 'apparaat')
                : t('notifications.components.devicePlural', 'apparaten')
        }`;
    }

    function onChangeDevices(
        context: BluemateStepperContext<AddNotificationValues>,
        value: string | null,
        selected: boolean
    ) {
        const {values, setValues} = context;
        if (value === null) {
            // Bulk select/deselect from the header controls.
            setValues({
                deviceIds: selected ? subscribedDevices.map(device => device.id) : [],
                thisDeviceSelected: selected ? syntheticOffered && canSubscribeHere : false
            });
            return;
        }
        if (value === THIS_DEVICE_VALUE) {
            if (!canSubscribeHere) return;
            setValues({thisDeviceSelected: selected});
            return;
        }
        if (!isDeviceOptionValue(value)) return;
        const id = value.slice('device:'.length);
        setValues({
            deviceIds: selected
                ? [...values.deviceIds.filter(v => v !== id), id]
                : values.deviceIds.filter(v => v !== id)
        });
    }

    function renderChannelsStep(context: BluemateStepperContext<AddNotificationValues>) {
        const {values, setValues} = context;
        stepContextRef.current = context;
        const selectedDeviceValues = [
            ...values.deviceIds.map(deviceOptionValue),
            ...(values.thisDeviceSelected ? [THIS_DEVICE_VALUE] : [])
        ];
        return (
            <>
                <div className='add-channels-banner'>
                    <AwesomeIcon name='circle-info' />
                    <div className='add-channels-banner-texts'>
                        <span className='add-channels-banner-title'>
                            {t('notifications.add.channelsTitle', 'Hoe wil je de notificaties ontvangen?')}
                        </span>
                        <span className='add-channels-banner-text'>
                            {t(
                                'notifications.add.channelsText',
                                'Kies hieronder één of meer kanalen. Kies je voor e-mail, dan kun je ook het e-mailadres instellen.'
                            )}
                        </span>
                    </div>
                </div>
                <div
                    className={classNames('add-channel-card', values.emailEnabled && 'selected')}
                    onClick={() => setValues({emailEnabled: !values.emailEnabled})}
                >
                    <div className='add-channel-icon'>
                        <AwesomeIcon name='envelope' />
                    </div>
                    <div className='add-channel-texts'>
                        <span className='add-channel-title'>
                            {t('notifications.add.emailChannelTitle', 'E-mail notificatie')}
                        </span>
                        <span className='add-channel-description'>
                            {t('notifications.add.emailChannelText', 'Ontvang notificaties per e-mail')}
                        </span>
                        {values.emailEnabled && (
                            <div className='add-channel-email' onClick={event => event.stopPropagation()}>
                                <BluemateInput
                                    onChange={(emailAddress: string) => setValues({emailAddress})}
                                    value={values.emailAddress}
                                    inputProps={{type: 'email'}}
                                    styled
                                />
                                {!values.emailAddress.trim() && (
                                    <div className='add-channel-hint'>
                                        <AwesomeIcon name='circle-info' />
                                        <span>
                                            {t(
                                                'notifications.add.emailRequiredHint',
                                                'Vul een e-mailadres in om deze notificatie te ontvangen.'
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <BluemateCheckbox checked={values.emailEnabled} className='add-channel-check' />
                </div>
                <div
                    className={classNames(
                        'add-channel-card',
                        values.pushEnabled && 'selected',
                        !pushPossible && 'disabled'
                    )}
                    onClick={() => {
                        if (!pushPossible) return;
                        setValues({pushEnabled: !values.pushEnabled});
                    }}
                >
                    <div className='add-channel-icon'>
                        <AwesomeIcon name={isMobileDevice() ? 'mobile-screen-button' : 'desktop'} />
                    </div>
                    <div className='add-channel-texts'>
                        <span className='add-channel-title'>
                            {t('notifications.add.pushChannelTitle', 'Push-notificatie')}
                        </span>
                        <span className='add-channel-description'>
                            {t(
                                'notifications.add.pushChannelDevicesText',
                                'Ontvang push-meldingen op de gekozen apparaten'
                            )}
                        </span>
                        {values.pushEnabled && (
                            <div className='add-channel-devices' onClick={event => event.stopPropagation()}>
                                <PortalSelectController
                                    value={selectedDeviceValues}
                                    onChange={(value, selected) => onChangeDevices(context, value, selected)}
                                    items={buildDeviceItems()}
                                    parseSelectValue={parseDevicesValue}
                                    disabled={submitting}
                                    multiple
                                    controls={['selectAll', 'deselectAll']}
                                    size='small'
                                    outlined
                                    itemsClassName='channels-select-items'
                                />
                                {!selectedDeviceValues.length && (
                                    <div className='add-channel-hint'>
                                        <AwesomeIcon name='circle-info' />
                                        <span>
                                            {t(
                                                'notifications.add.devicesRequiredHint',
                                                'Selecteer minimaal één apparaat om push-meldingen te ontvangen.'
                                            )}
                                        </span>
                                    </div>
                                )}
                                {syntheticOffered && !canSubscribeHere && (
                                    <div className='add-channel-hint'>
                                        <AwesomeIcon name='circle-info' />
                                        <span>
                                            {subscribedDevices.length > 0
                                                ? t(
                                                      'notifications.add.pushOtherDevicesHint',
                                                      'Op dit apparaat kan dat alleen in de Bluemate-app — je andere apparaten ontvangen de meldingen gewoon.'
                                                  )
                                                : t(
                                                      'push.installFirst',
                                                      'Meldingen zijn alleen beschikbaar in de Bluemate-app'
                                                  )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        {!pushPossible && (
                            <div className='add-channel-hint'>
                                <AwesomeIcon name='circle-info' />
                                <span>
                                    {t('push.installFirst', 'Meldingen zijn alleen beschikbaar in de Bluemate-app')}
                                </span>
                            </div>
                        )}
                    </div>
                    <BluemateCheckbox checked={values.pushEnabled} className='add-channel-check' />
                </div>
            </>
        );
    }

    /** Step 3 — ALWAYS shown after creation: confirmation + a test send
     *  through exactly the selected channels. */
    function renderSuccessStep(context: BluemateStepperContext<AddNotificationValues>) {
        const {values} = context;
        const both = values.emailEnabled && values.pushEnabled;
        // Every text on this step matches the ACTUAL channel selection —
        // e-mail only, push only, or both.
        const testLabel = both
            ? t('notifications.add.successTestBoth', 'Verstuur test e-mail + pushmelding')
            : values.pushEnabled
              ? t('notifications.add.successTestPush', 'Verstuur test pushmelding')
              : t('notifications.add.successTestMail', 'Verstuur test e-mail');
        const successText = both
            ? t(
                  'notifications.add.successTextBoth',
                  'Je ontvangt vanaf nu e-mail- en push-meldingen voor de ingestelde triggers.'
              )
            : values.pushEnabled
              ? t(
                    'notifications.add.successTextPush',
                    'Je ontvangt vanaf nu push-meldingen op de gekozen apparaten voor de ingestelde triggers.'
                )
              : t(
                    'notifications.add.successTextMail',
                    'Je ontvangt vanaf nu e-mailmeldingen voor de ingestelde triggers.'
                );
        const testTitle = both
            ? t(
                  'notifications.add.successTestTitleBoth',
                  'Controleer direct of de e-mail en pushberichten goed aankomen:'
              )
            : values.pushEnabled
              ? t('notifications.add.successTestTitlePush', 'Controleer direct of pushberichten goed aankomen:')
              : t('notifications.add.successTestTitleMail', 'Controleer direct of de e-mail goed aankomt:');
        return (
            <div className='add-success'>
                <div className='add-success-icon'>
                    <AwesomeIcon name={values.pushEnabled ? 'mobile-screen-button' : 'envelope'} />
                    <span className='add-success-badge'>
                        <AwesomeIcon name='circle-check' />
                    </span>
                </div>
                <h4 className='add-success-title'>{t('notifications.add.successTitle', 'Notificatie toegevoegd')}</h4>
                <p className='add-success-text'>{successText}</p>
                <div className='add-success-test'>
                    <span className='add-success-test-title'>{testTitle}</span>
                    <BluemateButton
                        label={
                            <>
                                <AwesomeIcon name='bell' /> {testLabel}
                            </>
                        }
                        onClick={() => void onSendTest(values)}
                        disabled={testSending}
                    />
                </div>
            </div>
        );
    }

    function renderDuplicateOverlay() {
        if (!pendingValues) return undefined;
        return (
            <div className='add-duplicate-overlay'>
                <div className='add-duplicate-icon'>
                    <AwesomeIcon name='triangle-exclamation' />
                </div>
                <h4 className='add-duplicate-title'>
                    {t('notifications.add.duplicateTitle', 'Notificatie bestaat al')}
                </h4>
                <p className='add-duplicate-text'>
                    {t(
                        'notifications.add.duplicateText',
                        'Er is al een actieve notificatie van dit type voor een of meer van de gekozen kanalen. Weet je zeker dat je deze extra notificatie wilt toevoegen? Dit kan dubbele meldingen opleveren.'
                    )}
                </p>
                <div className='add-duplicate-actions'>
                    <BluemateButton
                        tertiary
                        label={t('notifications.add.duplicateAdjust', 'Kanalen aanpassen')}
                        onClick={() => setPendingValues(null)}
                        disabled={submitting}
                    />
                    <BluemateButton
                        label={t('notifications.add.duplicateConfirm', 'Ja, voeg toe')}
                        onClick={() => pendingValues && void submit(pendingValues)}
                        disabled={submitting}
                    />
                </div>
            </div>
        );
    }

    const steps: Array<BluemateStepperStep<AddNotificationValues>> = [
        {
            key: 'type',
            title: t('notifications.add.headerTitle', 'Notificatie toevoegen'),
            subtitle: t('notifications.add.typeStepSubtitle', 'Kies een type notificatie'),
            headerAction: renderPremiumHeaderAction(),
            render: renderTypeStep,
            hideNext: true
        },
        {
            key: 'channels',
            title: t('notifications.add.channelsStepTitle', 'Kanalen & bestemmingen'),
            subtitle: t('notifications.add.channelsStepSubtitle', 'Instellingen voor bezorging'),
            render: renderChannelsStep,
            // At least one channel; a selected e-mail channel requires a valid
            // address; a selected push channel requires at least one device.
            isComplete: values =>
                (values.emailEnabled || values.pushEnabled) &&
                (!values.emailEnabled || validateEmail(values.emailAddress.trim())) &&
                (!values.pushEnabled || values.deviceIds.length > 0 || values.thisDeviceSelected),
            // The button CREATES the notification, then advances to the
            // always-present success step (see submit()). Short "Add" on mobile.
            nextLabel: responsiveLabel(
                t('notifications.add.finishLabel', 'Notificatie toevoegen'),
                t('notifications.add.finishLabelShort', 'Toevoegen')
            ),
            onNext: onCreate
        },
        {
            key: 'success',
            title: t('notifications.add.successStepTitle', 'Notificatie toegevoegd'),
            subtitle: t('notifications.add.successStepSubtitle', 'Bevestiging'),
            render: renderSuccessStep,
            // The notification is already created — no way back, no cancel;
            // the only exit is the Close button (or the dialog's X).
            hideBack: true,
            hideCancel: true,
            nextLabel: t('notifications.add.successClose', 'Sluiten'),
            onNext: () => setActive(false)
        }
    ];

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <BluemateStepper<AddNotificationValues>
            active={active}
            onClose={onClose}
            // Unreachable fallback: every step overrides its advance action
            // (type advances from its cards, channels creates via onNext, the
            // success step closes) — kept for the stepper's required prop.
            onFinish={() => setActive(false)}
            steps={steps}
            initialValues={{
                notificationType: null,
                emailEnabled: true,
                emailAddress: defaultEmail,
                pushEnabled: false,
                // "All devices" is the default: every subscribed device starts
                // selected; the subscribable current browser too (not on mobile
                // outside the installed app — the mobile push ban).
                deviceIds: subscribedDevices.map(device => device.id),
                thisDeviceSelected: syntheticOffered && canSubscribeHere
            }}
            finishLabel={t('notifications.add.finishLabel', 'Notificatie toevoegen')}
            // Short "Back" on mobile (only the channels step shows it).
            backLabel={responsiveLabel(
                t('controls.stepper.back', 'Vorige stap'),
                t('notifications.add.backShort', 'Terug')
            )}
            loading={submitting}
            className='add-notification'
            width='38rem'
            overlay={renderDuplicateOverlay()}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(AddNotification);
